import { useMutation, useQueryClient } from "@tanstack/react-query";
import { addMatrixRow, matrixQueries, type MatrixUi } from "@/domains/matrix";
import { optimisticRowId } from "../lib/optimistic-row";

type AddMatrixRowContext = {
  previous?: MatrixUi;
};

// Optimistic: creating a row writes an analysis, its settings and a step per prompt.
export function useAddMatrixRow(collectionId: string) {
  const queryClient = useQueryClient();
  const queryKey = matrixQueries.detail(collectionId).queryKey;
  const mutationKey = ["matrix", collectionId, "add-row"];

  return useMutation<
    { analysis_id: string },
    unknown,
    void,
    AddMatrixRowContext
  >({
    mutationKey,
    mutationFn: () => addMatrixRow(collectionId),
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData<MatrixUi>(queryKey);

      if (previous) {
        const position = previous.rows.length + 1;
        queryClient.setQueryData<MatrixUi>(queryKey, {
          ...previous,
          rows: [
            ...previous.rows,
            {
              analysisId: optimisticRowId(position),
              displayName: `Row ${position}`,
              status: "pending",
              createdAt: new Date().toISOString(),
              cells: Object.fromEntries(
                previous.columns.map((column) => [column.key, { value: "" }]),
              ),
            },
          ],
        });
      }

      return { previous };
    },
    onError: (_error, _input, context) => {
      if (context?.previous) {
        queryClient.setQueryData(queryKey, context.previous);
      }
    },
    // On success only: a failure restores the cache, and a refetch would hide the error.
    onSuccess: () => {
      // Only the last request refetches, or two quick adds make the grid twitch.
      if (queryClient.isMutating({ mutationKey }) > 1) return;
      void queryClient.invalidateQueries(matrixQueries.lists());
      void queryClient.invalidateQueries(matrixQueries.detail(collectionId));
    },
  });
}
